import { apiClient } from '@/shared/services/client'
import type {
  PaginatedClientes,
  ClienteDetail,
  ClienteCreatePayload,
  ClienteUpdatePayload,
  ClienteStats,
  FiltrosClientes,
  FiltrosDashboard,
  DashboardData,
  PaginatedHistorial,
  PaisOption,
  PaisCreatePayload,
  PaisUpdatePayload,
  CargoOption,
  CargoCreatePayload,
  CargoUpdatePayload,
  LineaOption,
  LineaCreatePayload,
  LineaUpdatePayload,
  LaboratorioOption,
  LaboratorioCreatePayload,
  LaboratorioDetailResponse,
  TipoIdentificacionOption,
  TipoLaboratorioOption,
  GrupoOption,
  BulkUploadResult,
  SuccessResponse,
  PaginatedLaboratorios,
  LaboratorioDetail,
  LaboratorioUpdatePayload,
  LaboratorioDashboardData,
  LaboratorioStats,
  FiltrosLaboratorios,
  TipoIdentificacionFull,
  TipoIdentificacionCreatePayload,
  TipoIdentificacionUpdatePayload,
  TipoLaboratorioFull,
  TipoLaboratorioCreatePayload,
  TipoLaboratorioUpdatePayload,
} from '../types'

const BASE = '/bd-clientes'

// === CLIENTES ===

export async function getClientes(params?: FiltrosClientes): Promise<PaginatedClientes> {
  const { data } = await apiClient.get(`${BASE}/`, { params })
  return data
}

export async function getCliente(id: number): Promise<ClienteDetail> {
  const { data } = await apiClient.get(`${BASE}/${id}/`)
  return data
}

export async function crearCliente(payload: ClienteCreatePayload): Promise<ClienteDetail> {
  const { data } = await apiClient.post(`${BASE}/`, payload)
  return data
}

export async function actualizarCliente(id: number, payload: ClienteUpdatePayload): Promise<ClienteDetail> {
  const { data } = await apiClient.put(`${BASE}/${id}/`, payload)
  return data
}

export async function eliminarCliente(id: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${BASE}/${id}/`)
  return data
}

// === HISTORIAL ===

export async function getHistorial(
  clienteId: number,
  params?: { page?: number; page_size?: number }
): Promise<PaginatedHistorial> {
  const { data } = await apiClient.get(`${BASE}/${clienteId}/historial/`, { params })
  return data
}

// === DASHBOARD ===

export async function getDashboard(params?: FiltrosDashboard): Promise<DashboardData> {
  const { data } = await apiClient.get(`${BASE}/dashboard/`, { params })
  return data
}

// === ESTADÍSTICAS ===

export async function getStats(params?: {
  pais_id?: number
  laboratorio_id?: number
  estado?: string
}): Promise<ClienteStats> {
  const { data } = await apiClient.get(`${BASE}/stats/`, { params })
  return data
}

// === EXPORTAR ===

export async function exportarClientes(params?: FiltrosClientes): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/exportar/`, {
    params,
    responseType: 'blob',
  })
  return data
}

// === CONFIGURACIÓN ===

export async function getPaises(activo?: boolean): Promise<PaisOption[]> {
  const params: Record<string, unknown> = {}
  if (activo !== undefined) params.activo = activo
  const { data } = await apiClient.get(`${BASE}/config/paises/`, { params })
  return data
}

export async function crearPais(payload: PaisCreatePayload): Promise<PaisOption> {
  const { data } = await apiClient.post(`${BASE}/config/paises/`, payload)
  return data
}

export async function actualizarPais(id: number, payload: PaisUpdatePayload): Promise<PaisOption> {
  const { data } = await apiClient.put(`${BASE}/config/paises/${id}/`, payload)
  return data
}

export async function eliminarPais(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/config/paises/${id}/`)
}

export async function getCargos(search?: string, activo?: boolean): Promise<CargoOption[]> {
  const params: Record<string, unknown> = {}
  if (search) params.search = search
  if (activo !== undefined) params.activo = activo
  const { data } = await apiClient.get(`${BASE}/config/cargos/`, { params })
  return data
}

export async function crearCargo(payload: CargoCreatePayload): Promise<CargoOption> {
  const { data } = await apiClient.post(`${BASE}/config/cargos/`, payload)
  return data
}

export async function actualizarCargo(id: number, payload: CargoUpdatePayload): Promise<CargoOption> {
  const { data } = await apiClient.put(`${BASE}/config/cargos/${id}/`, payload)
  return data
}

export async function eliminarCargo(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/config/cargos/${id}/`)
}

export async function getLineas(activo?: boolean): Promise<LineaOption[]> {
  const params: Record<string, unknown> = {}
  if (activo !== undefined) params.activo = activo
  const { data } = await apiClient.get(`${BASE}/config/lineas/`, { params })
  return data
}

export async function crearLinea(payload: LineaCreatePayload): Promise<LineaOption> {
  const { data } = await apiClient.post(`${BASE}/config/lineas/`, payload)
  return data
}

export async function actualizarLinea(id: number, payload: LineaUpdatePayload): Promise<LineaOption> {
  const { data } = await apiClient.put(`${BASE}/config/lineas/${id}/`, payload)
  return data
}

export async function eliminarLinea(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/config/lineas/${id}/`)
}

export async function getLaboratorios(paisId?: number): Promise<LaboratorioOption[]> {
  const { data } = await apiClient.get(`${BASE}/config/laboratorios/`, {
    params: paisId ? { pais_id: paisId } : undefined,
  })
  return data
}

export async function crearLaboratorio(payload: LaboratorioCreatePayload): Promise<LaboratorioDetailResponse> {
  const { data } = await apiClient.post(`${BASE}/config/laboratorios/`, payload)
  return data
}

export async function getTiposIdentificacion(): Promise<TipoIdentificacionOption[]> {
  const { data } = await apiClient.get(`${BASE}/config/tipos-identificacion/`)
  return data
}

export async function getTiposLaboratorio(): Promise<TipoLaboratorioOption[]> {
  const { data } = await apiClient.get(`${BASE}/config/tipos-laboratorio/`)
  return data
}

export async function getGrupos(): Promise<GrupoOption[]> {
  const { data } = await apiClient.get(`${BASE}/config/grupos/`)
  return data
}

export async function crearGrupo(payload: { name: string }): Promise<GrupoOption> {
  const { data } = await apiClient.post(`${BASE}/config/grupos/`, payload)
  return data
}

// === CARGA MASIVA ===

export async function uploadExcel(file: File): Promise<BulkUploadResult> {
  const formData = new FormData()
  formData.append('archivo', file)
  const { data } = await apiClient.post(`${BASE}/bulk/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function descargarPlantilla(): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/bulk/plantilla/`, {
    responseType: 'blob',
  })
  return data
}

// === LABORATORIOS ===

const LABS_BASE = '/bd-clientes/laboratorios'

export async function getLaboratoriosList(params?: FiltrosLaboratorios): Promise<PaginatedLaboratorios> {
  const { data } = await apiClient.get(`${LABS_BASE}/`, { params })
  return data
}

export async function getLaboratorio(id: number): Promise<LaboratorioDetail> {
  const { data } = await apiClient.get(`${LABS_BASE}/${id}/`)
  return data
}

export async function actualizarLaboratorioCompleto(id: number, payload: LaboratorioUpdatePayload): Promise<LaboratorioDetail> {
  const { data } = await apiClient.put(`${LABS_BASE}/${id}/`, payload)
  return data
}

export async function eliminarLaboratorioCompleto(id: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${LABS_BASE}/${id}/`)
  return data
}

export async function getLaboratoriosDashboard(params?: FiltrosLaboratorios): Promise<LaboratorioDashboardData> {
  const { data } = await apiClient.get(`${LABS_BASE}/dashboard/`, { params })
  return data
}

export async function getLaboratoriosStats(params?: Record<string, unknown>): Promise<LaboratorioStats> {
  const { data } = await apiClient.get(`${LABS_BASE}/stats/`, { params })
  return data
}

export async function exportarLaboratorios(params?: FiltrosLaboratorios): Promise<Blob> {
  const { data } = await apiClient.get(`${LABS_BASE}/exportar/`, {
    params,
    responseType: 'blob',
  })
  return data
}

export async function getHistorialLaboratorio(
  labId: number,
  params?: { page?: number; page_size?: number }
): Promise<PaginatedHistorial> {
  const { data } = await apiClient.get(`${LABS_BASE}/${labId}/historial/`, { params })
  return data
}

// Config Laboratorios

export async function getTiposIdentificacionFull(activo?: boolean): Promise<TipoIdentificacionFull[]> {
  const params: Record<string, unknown> = {}
  if (activo !== undefined) params.activo = activo
  const { data } = await apiClient.get(`${LABS_BASE}/config/tipos-identificacion/`, { params })
  return data
}

export async function crearTipoIdentificacion(payload: TipoIdentificacionCreatePayload): Promise<TipoIdentificacionFull> {
  const { data } = await apiClient.post(`${LABS_BASE}/config/tipos-identificacion/`, payload)
  return data
}

export async function actualizarTipoIdentificacion(id: number, payload: TipoIdentificacionUpdatePayload): Promise<TipoIdentificacionFull> {
  const { data } = await apiClient.put(`${LABS_BASE}/config/tipos-identificacion/${id}/`, payload)
  return data
}

export async function getTiposLaboratorioFull(activo?: boolean): Promise<TipoLaboratorioFull[]> {
  const params: Record<string, unknown> = {}
  if (activo !== undefined) params.activo = activo
  const { data } = await apiClient.get(`${LABS_BASE}/config/tipos-laboratorio/`, { params })
  return data
}

export async function crearTipoLaboratorioItem(payload: TipoLaboratorioCreatePayload): Promise<TipoLaboratorioFull> {
  const { data } = await apiClient.post(`${LABS_BASE}/config/tipos-laboratorio/`, payload)
  return data
}

export async function actualizarTipoLaboratorioItem(id: number, payload: TipoLaboratorioUpdatePayload): Promise<TipoLaboratorioFull> {
  const { data } = await apiClient.put(`${LABS_BASE}/config/tipos-laboratorio/${id}/`, payload)
  return data
}
